import { AppUser, IResponse, ProfilePicSizes } from '@eventr/common'
import { bs } from '../state/bound.store'
import {
    requestsEventrShared,
    requestsEventrSharedNoAuth,
    requestsItinerary,
} from './api'
import { gateWayInstance } from './axios'

type LocationData = {
    latitude: string
    longitude: string
    timeSharedUnix: string
}

interface IUserGenerate {
    email: string
}
export interface IUserInviteBody {
    users: IUserGenerate[]
    eventTitle: string
}

export interface IAddUserToItineraryBody {
    currentUserId: number
    newUserId: number
    itineraryId: number
    sections?: number[]
}

export interface INewUserInviteBody {
    users: IUserGenerate[]
    eventTitle: string
    itineraryId?: number
    userID?: number
    sections?: number[]
}

export interface IUpdateUserMetaBody {
    user_id: string
    user_meta: {
        first_name?: string
        last_name?: string
        phoneNumber?: string
        lastLocation?: LocationData | null
    }
}

export interface ICompleteProfileDTO {
    id: number
    firstName?: string
    lastName?: string
    phoneNumber?: string
    password?: string
    marketingOptIn?: boolean
    termsAccepted?: boolean
    countryCode?: string
    globalId?: string
}
export interface IUpdateUserDTO {
    id: number
    firstName?: string
    lastName?: string
    phoneNumber?: string
    password?: string
    email?: string
    roles?: string[]
    profilePicUrl?: string
    profilePicSizes?: ProfilePicSizes
    groupTags?: string[]
    departmentTags?: string[]
}

const userPrefix = 'profile'

export const UserAPI = {
    getUsers: async (): Promise<AppUser[]> => {
        const resp = await requestsItinerary.get<AppUser[]>('user/users', {
            NOT_IN_GUEST: true,
        })
        const user = resp.map((u) => {
            return { ...u, id: Number.parseInt(u.id.toString()) }
        })
        const isGuest = bs().isGuest
        // Filter out guest users if not a guest. So there is no chance of a user being able to see guests.
        if (!isGuest) {
            return user.filter((u) => !u.roles?.includes('guest'))
        }

        return user
    },

    getCurrentUser: async (): Promise<IResponse<AppUser>> => {
        const resp = await requestsEventrShared.get<IResponse<AppUser>>(
            'user/me',
            { NOT_IN_GUEST: true }
        )
        return resp
    },

    sendInviteNew: async (body: INewUserInviteBody): Promise<any> => {
        const resp = await requestsItinerary.post('generate-users', body)
        return resp
    },

    addUserToItinerary: async (body: IAddUserToItineraryBody): Promise<any> => {
        const resp = await requestsItinerary.post(
            'itinerary-add-user', body
        )
        return resp
    },

    getShareLink: async (
        itineraryID: number,
        user_id: number,
        guest?: boolean,
        sharePrefrence?: 'all' | 'some' | 'none',
        sectionsToInclude?: number[]
    ): Promise<any> => {
        const resp = await requestsItinerary.post(
            'generate-share-code/' + itineraryID + '/' + user_id,
            {
                guest: guest,
                sharingPreference: sharePrefrence,
                sectionsToUpdate: sectionsToInclude,
            }
        )
        return resp
    },

    getShareCode: async (code: string, user_id: string): Promise<any> => {
        const resp = await requestsItinerary.get(
            'share-codes/' + code + '/' + user_id
        )
        return resp
    },

    userExists: async (email: string): Promise<boolean> => {
        const resp = await gateWayInstance.get<boolean>(
            `${userPrefix}/exists/${email.toLowerCase()}`
        )

        return resp.data
    },

    /// user id required in body.
    updateUserMeta: async (body: IUpdateUserMetaBody): Promise<any> => {
        const resp = await requestsEventrShared.post(`usermeta/`, body)
        return resp
    },

    /// Uses the no auth request to remove the bearer token.
    completeProfile: async (
        profileComplete: ICompleteProfileDTO
    ): Promise<IResponse<string>> => {
        return requestsEventrSharedNoAuth.post(
            `user/completeProfile?user_id=${profileComplete.id}`,
            profileComplete
        )
    },

    updateProfile: async (
        profileUpdate: IUpdateUserDTO
    ): Promise<IResponse<string>> => {
        return requestsEventrShared.post(
            `user/update?user_id=${profileUpdate.id}`,
            profileUpdate
        )
    },
}
